import * as React from 'react';
import { Box, Button } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

/**
 * Segmented "pill" tabs — 1:1 with ClientApp2 CarList status filter (Activ/Inactive/Vandute):
 * a grey container holding one contained Button per option; selected = teal fill + white text,
 * unselected = transparent + dark text, hover = teal + white. Reusable across pages.
 */
export interface PillTabItem {
  label: React.ReactNode;
  value: string | number;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    width: '100%',
    gap: 2,
    backgroundColor: theme.palette.grey[600],
    padding: '0.5rem',
    borderRadius: theme.shape.borderRadius
  },
  tab: {
    textTransform: 'none',
    boxShadow: 'none',
    fontWeight: 400,
    minWidth: 'auto',
    padding: '0.5rem 0.75rem',
    borderRadius: 6,
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: theme.palette.primary.main, color: '#fff' }
  },
  tabSelected: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': { backgroundColor: theme.palette.primary.main, color: '#fff' }
  },
  endSlot: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center'
  }
}));

interface PillTabsProps {
  value: string | number;
  // Mirrors MUI Tabs onChange signature: (event, value).
  onChange: (event: React.ChangeEvent<any>, value: string | number) => void;
  tabs: PillTabItem[];
  className?: string;
  // Optional component rendered at the end of the bar (right side).
  endComponent?: React.ReactNode;
}

const PillTabs: React.FC<PillTabsProps> = ({ value, onChange, tabs, className, endComponent }) => {
  const classes = useStyles();
  return (
    <Box className={[classes.container, className].filter(Boolean).join(' ')}>
      {tabs.map((tab) => (
        <Button
          key={tab.value}
          disableElevation
          variant="contained"
          className={[classes.tab, value === tab.value ? classes.tabSelected : '']
            .filter(Boolean)
            .join(' ')}
          onClick={(event) => onChange(event as any, tab.value)}
        >
          {tab.label}
        </Button>
      ))}
      {endComponent && <Box className={classes.endSlot}>{endComponent}</Box>}
    </Box>
  );
};

export default PillTabs;
